// From: https://github.com/ifeanyiisitor/react-use-scroll-direction
// --> Changed some things to make it fit requirements, e.g. removing timeout for resetting isScrolling value.
import { useCallback, useEffect, useState } from 'react'
import {
  addScrollListener,
  getScrollLeft,
  getScrollTop,
  isBrowser,
  removeScrollListener,
} from '../utils/domUtils'

export type ScrollDirection = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT' | null

export interface ScrollDirectionHookResult {
  isScrolling: boolean
  isScrollingX: boolean
  isScrollingY: boolean
  isScrollingUp: boolean
  isScrollingDown: boolean
  isScrollingLeft: boolean
  isScrollingRight: boolean
  scrollDirection: ScrollDirection
  scrollTargetRef: (node: HTMLElement) => void
}

export function useScrollDirection(
  target?: HTMLElement
): ScrollDirectionHookResult {
  const [targetFromApi, setTargetFromApi] = useState<HTMLElement | undefined>()
  const [targetFromProps, setTargetFromProps] = useState<
    HTMLElement | undefined
  >()
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null)
  const targetToUse = targetFromProps || targetFromApi

  const isScrolling = scrollDirection !== null
  const isScrollingX = scrollDirection === 'LEFT' || scrollDirection === 'RIGHT'
  const isScrollingY = scrollDirection === 'UP' || scrollDirection === 'DOWN'
  const isScrollingUp = scrollDirection === 'UP'
  const isScrollingDown = scrollDirection === 'DOWN'
  const isScrollingLeft = scrollDirection === 'LEFT'
  const isScrollingRight = scrollDirection === 'RIGHT'

  const scrollTargetRef = useCallback((node: HTMLElement) => {
    setTargetFromApi(node)
  }, [])

  useEffect(() => {
    setTargetFromProps(target)
  }, [target])

  useEffect(() => {
    if (isBrowser()) {
      let lastScrollTop = getScrollTop(targetToUse)
      let lastScrollLeft = getScrollLeft(targetToUse)

      const handleScroll = () => {
        // Set vertical direction while scrolling
        const scrollTop = getScrollTop(targetToUse)
        if (scrollTop > lastScrollTop) {
          setScrollDirection('DOWN')
        } else if (scrollTop < lastScrollTop) {
          setScrollDirection('UP')
        }
        lastScrollTop = scrollTop

        // Set horizontal scroll direction
        const scrollLeft = getScrollLeft(targetToUse)
        if (scrollLeft > lastScrollLeft) {
          setScrollDirection('RIGHT')
        } else if (scrollLeft < lastScrollLeft) {
          setScrollDirection('LEFT')
        }
        lastScrollLeft = scrollLeft
      }

      addScrollListener(handleScroll, targetToUse)
      return () => removeScrollListener(handleScroll, targetToUse)
    }
  }, [targetToUse])

  return {
    isScrolling,
    isScrollingX,
    isScrollingY,
    isScrollingUp,
    isScrollingDown,
    isScrollingLeft,
    isScrollingRight,
    scrollDirection,
    scrollTargetRef,
  }
}
